import { useState } from "react";
import { ScrollArea } from "../components/ui/scroll-area";
import { Zap } from "lucide-react";

function readableParagraphs(body) {
  return String(body || "")
    .trim()
    .split(/\n+/)
    .flatMap((block) => {
      const sentences = block
        .match(/[^.!?]+(?:[.!?]+[”’"')\]]*|$)/g)
        ?.map((sentence) => sentence.trim())
        .filter(Boolean) || [];

      if (sentences.length <= 2) return block.trim() ? [block.trim()] : [];

      const paragraphs = [];
      let paragraph = "";
      let sentenceCount = 0;

      for (const sentence of sentences) {
        if (paragraph && (sentenceCount >= 2 || paragraph.length + sentence.length > 260)) {
          paragraphs.push(paragraph);
          paragraph = "";
          sentenceCount = 0;
        }
        paragraph = `${paragraph} ${sentence}`.trim();
        sentenceCount += 1;
      }

      if (paragraph) paragraphs.push(paragraph);
      return paragraphs;
    });
}

function KortNytItem({ item, isExpanded, onToggle }) {
  const paragraphs = readableParagraphs(item.body);

  return (
    <div
      className="px-4 py-3 hover:bg-[#FAFAFA] dark:hover:bg-zinc-900 transition-colors cursor-pointer focus:outline-none"
      onClick={onToggle}
      data-testid="kort-nyt-item"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
      aria-expanded={isExpanded}
    >
      <div className="flex items-center gap-2 mb-1.5">
        {item.time_text && (
          <span className="text-[10px] tracking-[0.2em] uppercase font-mono text-gray-400">
            {item.time_text}
          </span>
        )}
        {item.time_text && item.category && (
          <span className="text-gray-300 dark:text-zinc-700 text-[10px]">|</span>
        )}
        {item.category && (
          <span className="text-[10px] tracking-[0.2em] uppercase font-mono text-gray-500 dark:text-gray-400 font-semibold">
            {item.category}
          </span>
        )}
      </div>

      <h3 className="text-[13px] font-semibold leading-snug text-[#0A0A0A] dark:text-zinc-100 font-['Archivo']">
        {item.title}
      </h3>

      {isExpanded && item.body && (
        <div className="mt-3 space-y-3 text-sm leading-6 text-gray-600 dark:text-gray-400">
          {paragraphs.map((paragraph, index) => (
            <p key={`${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export function KortNyt({ items, loading }) {
  const [expandedKey, setExpandedKey] = useState(null);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="kort-nyt-loading">
        <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-gray-400">
          Henter kort nyt...
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col border-l border-[#E5E5E5] dark:border-zinc-800 bg-white dark:bg-zinc-950" data-testid="kort-nyt-panel">
      <div className="px-4 py-4 border-b border-[#E5E5E5] dark:border-zinc-800">
        <div className="flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-[#FF3B30]" strokeWidth={2} />
          <h2 className="text-xs tracking-[0.2em] uppercase font-mono font-semibold text-[#0A0A0A] dark:text-zinc-100">
            KORT NYT
          </h2>
        </div>
        <p className="text-[10px] font-mono text-gray-400 mt-1 tracking-wider">
          TV2 | Opdateres hvert 5. minut
        </p>
      </div>

      <ScrollArea className="flex-1">
        <div className="divide-y divide-[#F2F2F2] dark:divide-zinc-800">
          {items.map((item) => {
            const key = `${item.time_iso || item.time_text}::${item.title}`;
            return (
              <KortNytItem
                key={key}
                item={item}
                isExpanded={expandedKey === key}
                onToggle={() => setExpandedKey(expandedKey === key ? null : key)}
              />
            );
          })}
        </div>
      </ScrollArea>

      <div className="px-4 py-3 border-t border-[#E5E5E5] dark:border-zinc-800">
        <a
          href="https://nyheder.tv2.dk/live/kort-nyt"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] font-mono text-gray-400 hover:text-[#0A0A0A] dark:hover:text-zinc-50 transition-colors tracking-wider uppercase rounded-sm focus:outline-none px-2 py-1 -ml-2"
          data-testid="kort-nyt-source-link"
        >
          Se alle på TV2 &rarr;
        </a>
      </div>
    </div>
  );
}
